import { useEffect, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { useDebounce, useLanguage } from '../../../hooks';
import { useNestedLocationStore } from '../../../newStore/nestedLocationStore';
import { FlattenedLocationItem, NestedLocation } from '../../../types/nestedLocations';
import { cn } from '../../../utils/utils';
import { Checkbox } from '../../uiComponents/ComponentLibrary/Checkbox';
import {
  buildPreloadedLocationSelectorData,
  getFlattenedLocations,
  getLocationAncestors,
} from '../utils/LocationsHelpers';
import { LocationSelectorEmptyState } from './LocationSelectorEmptyState';
import { LocationSelectorRow, type ToggleAllChildrenShortcut } from './LocationSelectorRow';
import type { SelectedLocations, ToggleAllChildrenMode } from './LocationsSelectorReducer';

const ROW_HEIGHT = 60; // Approximate height of each row in pixels

/**
 * Target of a "toggle all children" shortcut click: the parent whose subtree is affected, and
 * the action (`selectAll` / `deselectAll`) the shortcut will dispatch.
 */
interface ToggleAllChildrenTarget {
  parentLocationId: number;
  mode: ToggleAllChildrenMode;
}

/** Arguments for `getToggleAllChildrenTargetForChild`. */
interface GetToggleAllChildrenTargetForChildArgs {
  child: FlattenedLocationItem | null;
  parent: FlattenedLocationItem | null;
  selectedLocations: SelectedLocations;
}

/** Arguments for `getToggleAllChildrenShortcutForRow`. */
interface GetToggleAllChildrenShortcutForRowArgs {
  item: FlattenedLocationItem;
  prevItem: FlattenedLocationItem | null;
  nextItem: FlattenedLocationItem | null;
  selectedLocations: SelectedLocations;
  enabled: boolean;
}

/**
 * Decides whether the "toggle all children" shortcut should appear between a given `parent` row
 * and its `child` row, and what action it should dispatch when clicked.
 *
 * The shortcut is a clickable link ("Seleccionar todos" / "Deseleccionar todos") shown above the
 * first child of a parent that is currently engaged in the group selection. It only applies when:
 *   - the child is a direct descendant of the parent (`parent_id` matches),
 *   - the parent is expanded and has at least one child (otherwise there is nothing to link over),
 *   - the child itself is not a tree root (roots never render shortcuts above them),
 *   - the parent is `selected` (→ the shortcut offers "deselect all") or `partialChildren` (→ the
 *     shortcut offers "select all" to re-collapse the subtree back into a single selection).
 *
 * Returns `null` when the shortcut should not appear; otherwise returns the `parentLocationId`
 * and `mode` the shortcut needs to dispatch on click.
 */
const getToggleAllChildrenTargetForChild = ({
  child,
  parent,
  selectedLocations,
}: GetToggleAllChildrenTargetForChildArgs): ToggleAllChildrenTarget | null => {
  if (!child || !parent) return null;
  if (child.isRootLocation) return null;
  if (child.location.parent_id !== parent.location.id) return null;
  if (!parent.isExpanded || parent.childrenCount === 0) return null;

  const parentStatus = selectedLocations.get(parent.location.id);
  if (parentStatus !== 'selected' && parentStatus !== 'partialChildren') return null;

  return {
    parentLocationId: parent.location.id,
    mode: parentStatus === 'selected' ? 'deselectAll' : 'selectAll',
  };
};

/**
 * Computes the per-row shortcut state that `LocationSelectorRow` needs to render correctly:
 *
 *   - `showAbove` + `parentLocationId` + `mode`: this row is the first child of an engaged parent,
 *     so it renders the shortcut on top of itself and knows which parent/action to dispatch.
 *   - `connectsWithShortcutBelow`: the *next* row will render a shortcut, so the current (parent)
 *     row must drop its bottom border so that the shortcut + first child visually merge into one
 *     block.
 *
 * Both checks reuse `getToggleAllChildrenTargetForChild`:
 *   - `above` is computed from the pair `(prev → item)` — does the shortcut belong above `item`?
 *   - `below` is computed from the pair `(item → next)` — does the shortcut belong above `next`?
 *
 * Root locations already manage their bottom border separately (rounded card styling), so they
 * are excluded from the `below` check to avoid double-styling.
 *
 * When `enabled` is false (the caller didn't provide `onToggleAllChildren`) the function
 * short-circuits with an empty state so no shortcut logic runs at all.
 */
const getToggleAllChildrenShortcutForRow = ({
  item,
  prevItem,
  nextItem,
  selectedLocations,
  enabled,
}: GetToggleAllChildrenShortcutForRowArgs): ToggleAllChildrenShortcut => {
  if (!enabled) {
    return { showAbove: false, connectsWithShortcutBelow: false };
  }

  const above = getToggleAllChildrenTargetForChild({
    child: item,
    parent: prevItem,
    selectedLocations,
  });
  const below = item.isRootLocation
    ? null
    : getToggleAllChildrenTargetForChild({
        child: nextItem,
        parent: item,
        selectedLocations,
      });

  return {
    showAbove: above !== null,
    connectsWithShortcutBelow: below !== null,
    parentLocationId: above?.parentLocationId,
    mode: above?.mode,
  };
};

interface LocationSelectorContentProps {
  selectedLocations: SelectedLocations;
  searchQuery: string;
  onSelect: (locationId: number) => void;
  allowedLocations?: NestedLocation[];
  showAltEmptyState?: boolean;
  multipleSelection?: boolean;
  allLocationsSelected?: boolean;
  onSelectAll?: () => void;
  onToggleAllChildren?: (parentLocationId: number, mode: ToggleAllChildrenMode) => void;
  locationsBlacklist?: number[];
  autoSelectChildren?: boolean;
  maxSelectableDepth?: number;
  contentClassName?: string;
  hasOldLocations?: boolean;
  customSearchNoResultsText?: string;
}

export function LocationSelectorContent({
  selectedLocations,
  searchQuery,
  onSelect,
  allowedLocations,
  showAltEmptyState = true,
  multipleSelection = true,
  allLocationsSelected = false,
  onSelectAll,
  onToggleAllChildren,
  autoSelectChildren = true,
  locationsBlacklist,
  maxSelectableDepth,
  contentClassName,
  hasOldLocations = false,
  customSearchNoResultsText,
}: LocationSelectorContentProps) {
  const lang = useLanguage();
  const { locationsIndex: nestedLocationIndex } = useNestedLocationStore();
  const [expandedLocations, setExpandedLocations] = useState<Record<number, boolean>>({});

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const debouncedSetQuery = useDebounce(() => setDebouncedSearchQuery(searchQuery), 200);

  useEffect(() => {
    debouncedSetQuery();
  }, [searchQuery]);

  const locationsIndex = useMemo(() => {
    return buildPreloadedLocationSelectorData(
      nestedLocationIndex.locationById,
      debouncedSearchQuery,
      allowedLocations,
    );
  }, [allowedLocations, nestedLocationIndex, debouncedSearchQuery]);

  const handleToggleExpand = (locationId: number) => {
    setExpandedLocations((prev) => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  const rootLocations = Array.from(locationsIndex.locationById.values()).filter(
    (location) => location.parent_id === null,
  );

  const selectedLocationAncestors = useMemo(() => {
    if (!multipleSelection) {
      const selectedLocationId = selectedLocations.keys().next().value;
      if (!selectedLocationId) return [];
      return getLocationAncestors(selectedLocationId, locationsIndex.locationById).map(
        (ancestor) => ancestor.id,
      );
    }
    return [];
  }, [selectedLocations, locationsIndex, multipleSelection]);

  // Flatten the nested locations into a flat array for virtualization
  const flattenedLocations = useMemo(() => {
    const flattened = getFlattenedLocations({
      rootLocations,
      searchQuery: debouncedSearchQuery,
      selectedLocations,
      expandedLocations,
      locationsIndex,
      allLocationsSelected,
      multipleSelection,
      autoSelectChildren,
      selectedLocationAncestors,
      locationsBlacklist,
    });

    // Apply maxSelectableDepth constraint if provided
    // If selecting a location at level L as parent, the child will be at level L + 1
    // So disable locations where level + 1 >= maxSelectableDepth
    if (maxSelectableDepth !== undefined && maxSelectableDepth > 0) {
      return flattened.map((item) => {
        const exceedsMaxDepth = item.level + 1 >= maxSelectableDepth;
        const isDisabled = exceedsMaxDepth;
        if (isDisabled) {
          return {
            ...item,
            location: {
              ...item.location,
              selectable: false,
            },
          };
        }
        return item;
      });
    }

    return flattened;
  }, [
    rootLocations,
    debouncedSearchQuery,
    selectedLocations,
    expandedLocations,
    locationsIndex,
    allLocationsSelected,
    multipleSelection,
    autoSelectChildren,
    selectedLocationAncestors,
    locationsBlacklist,
    maxSelectableDepth,
  ]);

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: flattenedLocations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const hasLocationsCreated = nestedLocationIndex.locationById.size > 0;
  const isSearchWithNoResults = hasLocationsCreated && debouncedSearchQuery.trim() !== '';

  if (locationsIndex.locationById.size === 0) {
    return (
      <LocationSelectorEmptyState
        variant={isSearchWithNoResults ? 'searchWithNoResults' : 'noLocationsCreated'}
        showAltEmptyState={showAltEmptyState}
        hasOldLocations={hasOldLocations}
        customSearchNoResultsText={customSearchNoResultsText}
      />
    );
  }

  return (
    <div className='flex flex-col flex-1'>
      {onSelectAll && (
        <div className='flex items-center justify-between gap-0.5 mb-3'>
          <h2 className='font-medium text-neutral-10'>{lang.form_builder.locations}</h2>
          <div>
            <Checkbox
              checked={allLocationsSelected}
              onChange={onSelectAll}
            />
            <span className='text-sm'>{lang.form_builder.selectAll}</span>
          </div>
        </div>
      )}
      <div
        ref={parentRef}
        className={cn('flex-1 overflow-auto mb-2', contentClassName)}
      >
        <div
          className='relative w-full'
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = flattenedLocations[virtualItem.index];
            const nextItem = flattenedLocations[virtualItem.index + 1] ?? null;
            const prevItem =
              virtualItem.index > 0 ? flattenedLocations[virtualItem.index - 1] : null;

            const toggleAllChildrenShortcut = getToggleAllChildrenShortcutForRow({
              item,
              prevItem,
              nextItem,
              selectedLocations,
              enabled: Boolean(onToggleAllChildren),
            });

            return (
              <LocationSelectorRow
                key={String(virtualItem.key)}
                virtualItem={virtualItem}
                item={item}
                multipleSelection={multipleSelection}
                nextItem={nextItem}
                virtualizer={virtualizer}
                onToggleExpand={handleToggleExpand}
                onSelect={(location) => onSelect(location.id)}
                toggleAllChildrenShortcut={toggleAllChildrenShortcut}
                onToggleAllChildren={onToggleAllChildren}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
